import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { IGroup } from '@shared/interfaces/group/group';
import { AlertService } from '@shared/services/alert.service';
import { ApiService } from '@shared/services/api.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-group-manage',
  templateUrl: './group-manage.component.html',
  styleUrls: ['./group-manage.component.scss']
})
export class GroupManageComponent implements OnInit {
  readonly guidEmpty = "00000000-0000-0000-0000-000000000000";

  @Input() deviceId!: string;
  @Input() canHasGroup = false;
  @Output() groupCreationMode = new EventEmitter();

  groupsList: IGroup[] = [];
  showInputGroup = false;
  previousGroup = "";
  selectedGroup = "";
  newGroupForm!: UntypedFormGroup;

  private subscription = new Subscription();

  constructor(
    private api: ApiService,
    private alertService: AlertService,
    private fb: UntypedFormBuilder
  ) {
    this.createForm();
  }

  ngOnInit() {
    if (this.canHasGroup){
      this.subscription.add(
        this.api.getDeviceGroup(this.deviceId).subscribe(res => {
          this.selectedGroup = res.id;
          this.previousGroup = res.id;
        })
      );
    }
   
    this.getGroupList();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  getGroupList(): void {
    this.subscription.add(
      this.api.getGroups().subscribe(res => {
        this.groupsList = res;
      })
    );
  }

  addDeviceToGroup(): void {
    if (this.previousGroup === this.selectedGroup) {
      return;
    }

    this.previousGroup = this.previousGroup === "" ? this.guidEmpty : this.previousGroup;
    this.selectedGroup = this.selectedGroup === "" ? this.guidEmpty : this.selectedGroup;

    this.api.putUserGroupDevice({ oldGroupId: this.previousGroup, newGroupId: this.selectedGroup, deviceId: this.deviceId })
      .subscribe({
        error: error => {},
        next: () => {
          if (this.selectedGroup === this.guidEmpty) {
            this.alertService.success( "device.alert-device-removed-from-group", 3000);
          } else {
            this.alertService.success( "groups.alert-device-assigned-to-group", 3000);
          }
        }
      });
  }

  createGroup(): Promise<void> {
    return new Promise((resolve) => {
      if (this.newGroupForm.valid) {
        this.api.postCreateGroup({ name: this.newGroupForm.value.groupName })
          .subscribe({ 
            next: res => {
              this.selectedGroup = res.id;
              this.alertService.success( "groups.alert-create-success", 3000);
              this.getGroupList();
              this.toggleInput();
              resolve();
            },
            error: error => resolve()
          });
      } else {
        resolve();
      }
    });
  }

  toggleInput(): void {
    this.showInputGroup = !this.showInputGroup;
    this.newGroupForm.reset();
    this.groupCreationMode.emit(this.showInputGroup);
  }

  createForm(): void {
    this.newGroupForm = this.fb.group({
      groupName: ["", Validators.compose([
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(30)
      ])]
    });
  }

  getFormControl = (name: string): UntypedFormControl => {
    return this.newGroupForm.get(name) as UntypedFormControl;
  }

  hasError(name: string): boolean {
    const e = this.getFormControl(name);
    return e.touched && !e.valid;
  }

  trackByFn(index: number, item: IGroup) {
    return item.id;
  }
}
